/** The search in the menu, the way Hugging Face and OpenRouter have one: models (the priced closed ones and
 *  the open-weight catalogue), clouds, and the question itself for Ask. `/` focuses it; arrows move; Enter takes
 *  the highlighted row. A model row offers Price and Compare; a cloud row opens its page where the automatic
 *  pages are on, else Compare. Nothing here computes anything: it only navigates. */
import { useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { VENDOR_LABEL, useSettings } from '../settings'
import type { CatalogModel, Provider } from '../types'
import { useAsync } from './useAsync'

type Row =
  | { kind: 'model'; canonical: string; label: string; meta: string }
  | { kind: 'cloud'; provider: Provider; label: string; meta: string }
  | { kind: 'ask'; q: string }

export function HeaderSearch({ onModel, onCloud, onAsk }: {
  onModel: (canonical: string, tool: 'calculator' | 'compare') => void
  onCloud: (provider: Provider) => void
  onAsk?: (q: string) => void
}) {
  const settings = useSettings()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState(0)
  const box = useRef<HTMLFormElement>(null)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 180); return () => clearTimeout(t) }, [q])
  // the closed models are not in the catalogue the search reads: they come from the priced list, once
  const closed = useAsync(() => api.models().then((all) => all.filter((m) => !m.open_weights)).catch(() => []), [])
  const found = useAsync<CatalogModel[]>(() => (debounced ? api.searchModels({ q: debounced, limit: 6 }) : Promise.resolve([])), [debounced])
  const needle = debounced.toLowerCase()
  const rows: Row[] = []
  if (needle) {
    for (const m of (closed.data ?? []).filter((m) => m.canonical.toLowerCase().includes(needle) || m.label.toLowerCase().includes(needle)).slice(0, 3)) {
      rows.push({ kind: 'model', canonical: m.canonical, label: m.label, meta: 'sold per token' })
    }
    for (const m of (found.data ?? []).filter((m) => !rows.some((r) => r.kind === 'model' && r.canonical === m.canonical)).slice(0, 6 - rows.length)) {
      rows.push({ kind: 'model', canonical: m.canonical, label: m.canonical.split('/').pop() ?? m.canonical,
        meta: `${m.params_b != null ? `${m.params_b}B · ` : ''}${m.sold_by_cloud ? 'sold per token' : 'self-host'}` })
    }
    for (const p of settings.vendors.filter((p) => VENDOR_LABEL[p].toLowerCase().includes(needle) || p.includes(needle)).slice(0, 2)) {
      rows.push({ kind: 'cloud', provider: p, label: VENDOR_LABEL[p], meta: 'every model and instance it sells' })
    }
    if (onAsk) rows.push({ kind: 'ask', q: debounced })
  }
  useEffect(() => { setAt(0) }, [debounced])
  useEffect(() => {
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    const slash = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey) { e.preventDefault(); input.current?.focus() }
    }
    document.addEventListener('mousedown', away); document.addEventListener('keydown', slash)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', slash) }
  }, [])
  const take = (r: Row, tool: 'calculator' | 'compare' = 'compare') => {
    setOpen(false); setQ('')
    if (r.kind === 'model') onModel(r.canonical, tool)
    else if (r.kind === 'cloud') onCloud(r.provider)
    else onAsk?.(r.q)
  }
  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setAt((x) => Math.min(rows.length - 1, x + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((x) => Math.max(0, x - 1)) }
    else if (e.key === 'Escape') { setOpen(false); input.current?.blur() }
  }
  const show = open && needle !== ''
  return (
    <form className="hsearch" ref={box} role="search" onSubmit={(e) => { e.preventDefault(); if (rows.length) take(rows[Math.min(at, rows.length - 1)]) }}>
      <span className="ico" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      </span>
      <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)} onKeyDown={key}
        placeholder="Search models, clouds… or ask" aria-label="Search models and clouds, or ask a question"
        role="combobox" aria-expanded={show} aria-controls="hsearch-list" aria-autocomplete="list" autoComplete="off" />
      <kbd aria-hidden="true">/</kbd>
      {show ? (
        <ul id="hsearch-list" role="listbox">
          {rows.map((r, i) => (
            <li key={r.kind === 'model' ? r.canonical : r.kind === 'cloud' ? r.provider : 'ask'} role="option" aria-selected={i === at}
              className={r.kind} onMouseEnter={() => setAt(i)} onMouseDown={(e) => { e.preventDefault(); take(r) }}>
              {r.kind === 'ask' ? (
                <><span className="nm">“{r.q}”</span><span className="meta">ask it in plain words</span><span className="acts"><span>Ask ↵</span></span></>
              ) : (
                <>
                  <span className="nm">{r.label}</span><span className="meta">{r.meta}</span>
                  {r.kind === 'model' ? (
                    <span className="acts">
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); take(r, 'calculator') }}>Price</button>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); take(r, 'compare') }}>Compare</button>
                    </span>
                  ) : <span className="acts"><span>Open</span></span>}
                </>
              )}
            </li>
          ))}
          {!rows.length ? <li className="none muted small">{found.loading ? 'searching…' : `Nothing matches “${debounced}”.`}</li> : null}
        </ul>
      ) : null}
    </form>
  )
}
